import { useState, useEffect } from 'react';
import { isPhoneLikeDevice } from '../utils/deviceLayout';

function detectMobileLayout(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768 || isPhoneLikeDevice();
}

interface PWAStatus {
  isStandalone: boolean;
  isInstalled: boolean;
  canInstall: boolean;
  isOnline: boolean;
  isMobile: boolean;
}

export const usePWA = (): PWAStatus => {
  const [pwaStatus, setPwaStatus] = useState<PWAStatus>({
    isStandalone: false,
    isInstalled: false,
    canInstall: false,
    isOnline: navigator.onLine,
    isMobile: detectMobileLayout(),
  });

  useEffect(() => {
    // Check if app is in standalone mode (PWA)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone === true;

    // Check if app can be installed
    const canInstall = 'serviceWorker' in navigator && 'PushManager' in window;

    // Check if app is already installed
    const isInstalled = isStandalone || localStorage.getItem('pwa-installed') === 'true';

    // Update status
    setPwaStatus(prev => ({
      ...prev,
      isStandalone,
      isInstalled,
      canInstall,
    }));

    // Listen for online/offline status
    const handleOnline = () => setPwaStatus(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setPwaStatus(prev => ({ ...prev, isOnline: false }));

    // Listen for window resize
    const handleResize = () => {
      setPwaStatus(prev => ({ ...prev, isMobile: detectMobileLayout() }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('resize', handleResize);

    // Mark as installed if in standalone mode
    if (isStandalone) {
      localStorage.setItem('pwa-installed', 'true');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return pwaStatus;
};

// Hook for PWA installation
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const installPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
      }
      
      setDeferredPrompt(null);
    }
  };

  return { canInstall: !!deferredPrompt, installPWA };
}; 